import { useCallback, useEffect, useState } from "react";
import { api, apiError, fmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PlugZap, RefreshCw, ShieldCheck } from "lucide-react";

const Code = ({ children }) => (
  <pre className="mono mt-3 overflow-auto border border-border bg-secondary p-4 text-xs text-foreground">
    {children}
  </pre>
);

export const FiniIngest = () => {
  const [f, setF] = useState(null);
  const [test, setTest] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/fini/status");
      setF(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (path, label) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/fini/${path}`);
      setTest(data);
      toast.success(`${label} succeeded`);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setTest(null);
    } finally {
      setBusy(false);
    }
  };

  if (!f) return null;

  return (
    <div className="mt-4 border border-border bg-card p-8" data-testid="fini-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <PlugZap className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Fini Apollo ingest</h3>
          <Badge
            className="rounded-none uppercase"
            variant={f.configured ? "default" : "secondary"}
            data-testid="fini-configured-badge"
          >
            {f.configured ? (f.poll_enabled ? "polling" : "keys set · paused") : "not configured"}
          </Badge>
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="rounded-none"
            disabled={busy}
            onClick={() => run("test", "Key check")}
            data-testid="fini-test-btn"
          >
            <ShieldCheck className="mr-2 h-4 w-4" /> Test connection
          </Button>
          <Button
            className="rounded-none"
            disabled={busy}
            onClick={() => run("poll", "Poll")}
            data-testid="fini-poll-btn"
          >
            <RefreshCw className="mr-2 h-4 w-4" /> Poll players now
          </Button>
        </div>
      </div>

      <p className="mt-4 max-w-3xl text-sm text-muted-foreground">
        Apollo has no playtime endpoint, so we poll <span className="mono">POST /api/v3/apollo</span> with{" "}
        <span className="mono">get:players</span> every {f.poll_seconds}s and credit one minute to every
        connected player. Nothing needs installing on your game server.
      </p>

      <div className="mt-6 grid gap-4 md:grid-cols-4">
        <div className="border border-border p-5" data-testid="fini-stat-online">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Players last poll</div>
          <div className="mono mt-3 text-2xl">{fmt(f.status?.players_online ?? 0)}</div>
        </div>
        <div className="border border-border p-5" data-testid="fini-stat-credited">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Credited last poll</div>
          <div className="mono mt-3 text-2xl">{fmt(f.status?.credited ?? 0)}</div>
        </div>
        <div className="border border-border p-5" data-testid="fini-stat-minutes">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Minutes via Fini 24h</div>
          <div className="mono mt-3 text-2xl">{fmt(f.minutes_from_fini_24h)}</div>
        </div>
        <div className="border border-border p-5" data-testid="fini-stat-last">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Last poll</div>
          <div className="mt-3 text-sm">
            {f.status?.last_poll_at ? new Date(f.status.last_poll_at).toLocaleTimeString() : "never"}
          </div>
        </div>
      </div>

      {f.status?.error && (
        <div className="mt-4 border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive" data-testid="fini-error">
          {f.status.error}
        </div>
      )}

      {!f.configured && (
        <div className="mt-6" data-testid="fini-setup">
          <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Enable it</h4>
          <ol className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li>
              1. At <span className="mono">fini.dev/profile</span> → API keys → Add new, pick your server and
              grant <span className="mono">APOLLO.VIEW</span>. Copy the secret key immediately.
            </li>
            <li>2. Put the values in <span className="mono">backend/.env</span>:</li>
          </ol>
          <Code>{`FINI_ACCESS_KEY="your_access_key"
FINI_SECRET_KEY="your_secret_key"
FINI_SERVER_IP="123.45.67.89"
FINI_POLL_ENABLED="true"
FINI_POLL_SECONDS="60"`}</Code>
          <p className="mt-3 text-xs text-muted-foreground">
            Then restart the backend and hit Test connection. Rate limit is 100 requests/minute, so a 60s
            poll uses about 1.
          </p>
        </div>
      )}

      {f.status?.unlinked?.length > 0 && (
        <div className="mt-6" data-testid="fini-unlinked">
          <h4 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Online but not linked — give them these codes
          </h4>
          <div className="mt-3 space-y-2 text-sm">
            {f.status.unlinked.map((u) => (
              <div key={u.license_id} className="flex items-center justify-between border-b border-border pb-2">
                <span className="truncate">{u.name || u.license_id}</span>
                <span className="mono text-primary">{u.code}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(test || f.status?.sample) && (
        <details className="mt-6" data-testid="fini-raw">
          <summary className="cursor-pointer text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground">
            Raw Apollo payload
          </summary>
          <Code>{JSON.stringify(test || f.status.sample, null, 2).slice(0, 4000)}</Code>
        </details>
      )}
    </div>
  );
};
